import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { 
  Truck, Package, Bell, 
  DollarSign, ChevronLeft, ChevronRight, Menu, Bookmark
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DashboardCard, LoadItem } from '../components/DashboardComponents';
import { logoutUser } from '../store/slices/authSlice';
import { fetchLoadFeed, fetchSavedLoads, setCurrentView } from '../store/slices/loadFeedSlice';
import api from '../utils/api';
import SortFilterControls from '../components/SortFilterControls';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { useLoadExpiryTracker } from '../hooks/useLoadExpiryTracker';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { soundManager } from '../utils/soundManager';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import ActiveLoadsMap from '../components/ActiveLoadsMap';

// Reusable UI class tokens for cohesive, minimalist styling
const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const selectClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const textareaClass = 'w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const buttonPrimary = 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const buttonSecondary = 'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all';
const iconButton = 'p-2 rounded-md bg-white shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const cardClass = 'bg-white rounded-lg border border-gray-200 shadow-sm';

const CarrierDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { loads, status, error, pagination } = useSelector((state) => state.loadFeed);
  const socket = useSelector((state) => state.websocket.socket);
  const [currentPage, setCurrentPage] = useState(1);
  const [greeting, setGreeting] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [sortOption, setSortOption] = useState('date_desc');
  const [filterOption, setFilterOption] = useState('all');
  const [sortedAndFilteredLoads, setSortedAndFilteredLoads] = useState([]);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [soundEnabled, setSoundEnabled] = useState(soundManager.enabled);
  const savedLoads = useSelector(state => state.loadFeed.savedLoads);
  const currentView = useSelector(state => state.loadFeed.currentView);
  const [savedLoadsFetched, setSavedLoadsFetched] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  
  // Track load expiry and automatically remove expired loads from the feed in real-time
  // Uses server-synced time (via WebSocket heartbeat) for accurate expiry tracking
  useLoadExpiryTracker(loads, { showToast: true });

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/api/carrier/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) setGreeting('Good morning');
      else if (hour < 18) setGreeting('Good afternoon');
      else setGreeting('Good evening');
    };

    updateGreeting();
    const timer = setInterval(updateGreeting, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (filterOption === 'saved') {
      dispatch(fetchSavedLoads({ page: currentPage }));
    } else {
      dispatch(fetchLoadFeed({ page: currentPage }));
    }
    
    if (!savedLoadsFetched) {
      dispatch(fetchSavedLoads({ page: 1 }));
      setSavedLoadsFetched(true);
    }
    
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage, filterOption, savedLoadsFetched]);

  useEffect(() => {
    fetchDashboardData();

    if (socket) {
      // Set up WebSocket event listeners
      socket.on('new_load', fetchDashboardData);
      socket.on('update_load', fetchDashboardData);
      socket.on('delete_load', fetchDashboardData);
      socket.on('new_bid', fetchDashboardData);
      socket.on('update_bid', fetchDashboardData);

      // Clean up event listeners
      return () => {
        socket.off('new_load', fetchDashboardData);
        socket.off('update_load', fetchDashboardData);
        socket.off('delete_load', fetchDashboardData);
        socket.off('new_bid', fetchDashboardData);
        socket.off('update_bid', fetchDashboardData);
      };
    }
  }, [socket, fetchDashboardData]);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    if (socket) {
      const handleNewLoad = async (data) => {
        console.log('New load received');
        try {
          await fetchDashboardData();
        } catch (error) {
          console.error('Error handling new load:', error);
        }
      };

      socket.on('new_load', handleNewLoad);
      console.log('WebSocket listener for new_load registered');

      return () => {
        socket.off('new_load', handleNewLoad);
      };
    }
  }, [socket, fetchDashboardData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSort = (option) => {
    setSortOption(option);
  };

  const handleFilter = (option) => {
    setFilterOption(option);
    setCurrentPage(1);
    if (option === 'saved') {
      dispatch(setCurrentView('saved'));
    } else {
      dispatch(setCurrentView('feed'));
      dispatch(fetchLoadFeed({ page: 1 }));
    }
  };

  const sortLoads = useCallback((loadsToSort, sortOption) => {
    return [...loadsToSort].sort((a, b) => {
      switch (sortOption) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'weight_desc':
          return b.weight - a.weight;
        case 'weight_asc':
          return a.weight - b.weight;
        case 'id_asc':
          return a.id - b.id;
        case 'id_desc':
          return b.id - a.id;
        default:
          return 0;
      }
    });
  }, []);

  const filterLoads = useCallback((loadsToFilter, filterOption) => {
    if (filterOption === 'saved') {
      return savedLoads;
    }
    
    switch (filterOption) {
      case 'all':
        return loadsToFilter;
      default:
        return loadsToFilter.filter(load => load.status.toLowerCase() === filterOption);
    }
  }, [savedLoads]);

  useEffect(() => {
    let filteredLoads = filterLoads(loads, filterOption);
    let sortedLoads = sortLoads(filteredLoads, sortOption);
    setSortedAndFilteredLoads(sortedLoads);
  }, [loads, sortOption, filterOption, sortLoads, filterLoads]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const toggleSoundNotifications = async () => {
    try {
      const result = await soundManager.toggle();
      setSoundEnabled(result);
      
      if (result) {
        // Show success message
        // You might want to add a toast notification here
        console.log('Sound notifications enabled');
      } else {
        // Show message about needing to interact first
        console.log('Please interact with the page to enable sound notifications');
      }
    } catch (error) {
      console.error('Error toggling sound:', error);
    }
  };

  const NotificationToggle = () => (
    <button
      onClick={toggleSoundNotifications}
      className={`${iconButton} flex items-center gap-2`}
      title="Toggle sound notifications"
    >
      {soundEnabled ? (
        <Bell className="h-5 w-5 text-blue-500" />
      ) : (
        <Bell className="h-5 w-5 text-gray-400" />
      )}
      <span className="hidden sm:inline">
        {soundEnabled ? 'Sound On' : 'Sound Off'}
      </span>
    </button>
  );

  // Update the loads display logic
  const displayedLoads = useMemo(() => {
    let loadsToDisplay = filterOption === 'saved' ? savedLoads : loads;
    
    // Add is_saved flag to loads by checking against savedLoads
    loadsToDisplay = loadsToDisplay.map(load => ({
      ...load,
      is_saved: savedLoads.some(savedLoad => savedLoad.id === load.id)
    }));
    
    // Filter out saved loads from the main feed
    if (filterOption !== 'saved') {
      loadsToDisplay = loadsToDisplay.filter(load => !load.is_saved);
    }
    
    return sortLoads(loadsToDisplay, sortOption);
  }, [filterOption, savedLoads, loads, sortOption, sortLoads]);

  // Memoize saved loads count
  const savedLoadsCount = useMemo(() => savedLoads.length, [savedLoads]);

  // Add a separate filter section for saved loads
  const renderFilterControls = () => (
    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
      {/* View Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => handleFilter('all')}
          className={`${filterOption === 'all' ? buttonPrimary : buttonSecondary}`}
        >
          All Loads
        </button>
        <button
          onClick={() => handleFilter('saved')}
          className={`${filterOption === 'saved' ? buttonPrimary : buttonSecondary} flex items-center gap-2`}
        >
          <Bookmark className={`w-4 h-4 ${filterOption === 'saved' ? 'fill-current' : ''}`} />
          Saved Loads
          {savedLoadsCount > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              filterOption === 'saved' 
                ? 'bg-white text-blue-600' 
                : 'bg-blue-600 text-white'
            }`}>
              {savedLoadsCount}
            </span>
          )}
        </button>
      </div>

      {/* Status Filters - Only show when viewing all loads */}
      {filterOption === 'all' && (
        <SortFilterControls
          sortOption={sortOption}
          filterOption={filterOption}
          onSort={handleSort}
          onFilter={handleFilter}
          filterOptions={[
            { value: 'open', label: 'Open' },
            { value: 'pending', label: 'Pending' },
            { value: 'assigned', label: 'Assigned' },
            { value: 'completed', label: 'Completed' }
          ]}
          className="flex flex-col sm:flex-row gap-2 sm:gap-4"
        />
      )}
    </div>
  );

  const handleSaveLoad = async (load) => {
    try {
      const method = load.is_saved ? 'DELETE' : 'POST';
      await api.request(`/api/loads/${load.id}/save`, { method });
      
      toast.success(load.is_saved ? 'Load removed from saved' : 'Load saved successfully');
      
      // WebSocket will handle the state update
    } catch (error) {
      console.error('Error toggling load save:', error);
      toast.error('Failed to save load');
    }
  };

  const toggleMapExpand = () => {
    setIsMapExpanded(!isMapExpanded);
  };

  if (status === 'loading' || !dashboardData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="bg-gray-100 min-h-screen flex">
      <SideNavigation 
        isOpen={sidebarOpen} 
        onToggle={toggleSidebar}
      />

      {/* Main Content */}
      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0 px-2' : (sidebarOpen ? 'ml-64' : 'ml-16')
      } ${isMapExpanded ? 'overflow-hidden' : ''}`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4 px-2">
            {(isMobile || isTablet) && (
              <button
                onClick={toggleSidebar}
                className={iconButton}
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 ml-2">
              {greeting}, {user.firstName}!
            </h1>
            <div className="flex items-center gap-2">
              <NotificationToggle />
            </div>
          </div>
          
          {/* Dashboard Cards - Streamlined 3-card layout */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6 px-2">
            <DashboardCard icon={Truck} title="Active Loads" value={dashboardData.active_loads} />
            <DashboardCard 
              icon={DollarSign} 
              title="Earnings (MTD)" 
              value={`$${dashboardData.earnings_mtd.toLocaleString()}`} 
              trend={dashboardData.earnings_trend >= 0 ? "up" : "down"} 
              trendValue={`${Math.abs(dashboardData.earnings_trend)}%`} 
            />
            <DashboardCard icon={Package} title="Completed" value={dashboardData.completed_deliveries} />
          </div>

          {/* Active Loads Map */}
          {!isMapExpanded && (
            <ActiveLoadsMap 
              expanded={isMapExpanded} 
              onToggleExpand={toggleMapExpand} 
            />
          )}

          {/* Sort and Filter Controls */}
          <div className="px-2 mb-4">
            {renderFilterControls()}
          </div>

          {/* Load List */}
          <div className="space-y-4 px-2">
            {displayedLoads.length > 0 ? (
              displayedLoads.map((load) => (
                <LoadItem
                  key={load.id}
                  load={load}
                  user={user}
                  onSaveLoad={handleSaveLoad}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {filterOption === 'saved' 
                    ? 'No saved loads found' 
                    : 'No loads found matching your criteria'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="flex justify-center items-center mt-6 px-2">
              <nav className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`${iconButton} disabled:opacity-50`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <span className="px-4 py-2 text-sm">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className={`${iconButton} disabled:opacity-50`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </nav>
            </div>
          )}
        </div>
      </main>

      {/* Expanded Map (rendered outside of main when expanded) */}
      {isMapExpanded && (
        <ActiveLoadsMap 
          expanded={isMapExpanded} 
          onToggleExpand={toggleMapExpand} 
        />
      )}

      {/* Add ToastContainer here */}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={3}
      />
    </div>
  );
};

export default CarrierDashboard;
